import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import dayjs from 'dayjs';
import QRCode from 'qrcode';
import {
  DEFAULT_ATTACHMENT_FOLDER,
  DEFAULT_DAILY_FOLDER,
  DEFAULT_DAILY_FORMAT,
  DEFAULT_TEMPLATES_FOLDER,
  dailyNotePath,
  type VaultSettings,
} from '@rbv/shared';
import { useApp } from '../store.js';
import { vault } from '../sync/vault.js';
import { listTemplates } from './templates.js';
import { ApiAccess } from './ApiAccess.js';
import { EmailIngest } from './EmailIngest.js';
import './features.css';

/** Keep in sync with apps/web/package.json (no build-time define available). */
const APP_VERSION = '0.1.0';
const REPO_URL = 'https://github.com/codedebate/rbv-obsidian';

type StringSettingKey =
  | 'dailyFolder'
  | 'dailyFormat'
  | 'dailyTemplate'
  | 'templatesFolder'
  | 'attachmentFolder';

export function SettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return <SettingsSheet onClose={onClose} />;
}

function SettingsSheet({ onClose }: { onClose: () => void }) {
  const username = useApp((s) => s.username);
  const [settings, setSettings] = useState<VaultSettings>(() => {
    try {
      return vault.settings;
    } catch {
      return {};
    }
  });
  const [templates] = useState(() => listTemplates());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const update = (key: StringSettingKey, raw: string) => {
    const value = raw.trim() === '' ? undefined : raw;
    setSettings((s) => ({ ...s, [key]: value }));
    try {
      vault.setSetting(key, value);
    } catch {
      // vault not started (shouldn't happen once the shell is up)
    }
  };

  let dailyPreview: string;
  try {
    dailyPreview = dailyNotePath(settings);
  } catch {
    dailyPreview = dayjs().format(DEFAULT_DAILY_FORMAT);
  }

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div
        className="settings-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="settings-header">
          <h2>Settings</h2>
          <button type="button" className="sd-close" aria-label="Close settings" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="settings-body">
          <section className="settings-section">
            <h3>Vault</h3>
            <Field
              label="Daily note folder"
              value={settings.dailyFolder ?? ''}
              placeholder={DEFAULT_DAILY_FOLDER}
              onChange={(v) => update('dailyFolder', v)}
            />
            <Field
              label="Daily note format"
              value={settings.dailyFormat ?? ''}
              placeholder={DEFAULT_DAILY_FORMAT}
              mono
              onChange={(v) => update('dailyFormat', v)}
              hint={`Today: ${dailyPreview}`}
            />
            <div className="settings-field">
              <label htmlFor="sd-daily-template">Daily note template</label>
              <select
                id="sd-daily-template"
                value={settings.dailyTemplate ?? ''}
                onChange={(e) => update('dailyTemplate', e.target.value)}
              >
                <option value="">None</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.path}>
                    {t.name}
                  </option>
                ))}
              </select>
              {templates.length === 0 && (
                <div className="sd-hint">
                  No templates yet — create notes in “
                  {settings.templatesFolder || DEFAULT_TEMPLATES_FOLDER}”.
                </div>
              )}
            </div>
            <Field
              label="Templates folder"
              value={settings.templatesFolder ?? ''}
              placeholder={DEFAULT_TEMPLATES_FOLDER}
              onChange={(v) => update('templatesFolder', v)}
            />
            <Field
              label="Attachment folder"
              value={settings.attachmentFolder ?? ''}
              placeholder={DEFAULT_ATTACHMENT_FOLDER}
              onChange={(v) => update('attachmentFolder', v)}
            />
          </section>

          <section className="settings-section">
            <h3>Account</h3>
            <div className="sd-account-row">
              <span>
                Signed in as <strong>{username ?? 'unknown'}</strong>
              </span>
              <button
                type="button"
                className="sd-btn danger"
                onClick={() => void logout()}
              >
                Log out
              </button>
            </div>
            <AddUserFlow />
          </section>

          <ApiAccess />

          <EmailIngest />

          <section className="settings-section">
            <h3>About</h3>
            <p className="sd-about">
              RBV Notes v{APP_VERSION} — self-hosted markdown knowledge graph.
              <br />
              <a href={REPO_URL} target="_blank" rel="noreferrer">
                Source on GitHub
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  placeholder,
  hint,
  mono,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  hint?: string;
  mono?: boolean;
  onChange: (v: string) => void;
}) {
  const id = `sd-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div className="settings-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        value={value}
        placeholder={placeholder}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className={mono ? 'mono' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <div className="sd-hint">{hint}</div>}
    </div>
  );
}

async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST' });
  } catch {
    // offline — reload will land on the login page once back online
  }
  location.reload();
}

/**
 * Enroll a second account (future co-editing): same two-step flow as
 * first-run Setup, but against the authed /api/auth/setup endpoints.
 */
function AddUserFlow() {
  const [step, setStep] = useState<'closed' | 'form' | 'totp' | 'done'>('closed');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [secret, setSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [createdUser, setCreatedUser] = useState('');

  const reset = () => {
    setStep('closed');
    setUsername('');
    setPassword('');
    setSetupToken('');
    setSecret('');
    setQrDataUrl('');
    setCode('');
    setError('');
  };

  const begin = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/auth/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const body = (await res.json()) as {
        error?: string;
        setupToken?: string;
        secret?: string;
        otpauthUrl?: string;
      };
      if (!res.ok || !body.setupToken || !body.secret || !body.otpauthUrl) {
        setError(body.error ?? 'setup failed');
        return;
      }
      setSetupToken(body.setupToken);
      setSecret(body.secret);
      setQrDataUrl(await QRCode.toDataURL(body.otpauthUrl, { margin: 1, width: 200 }).catch(() => ''));
      setStep('totp');
    } catch {
      setError('network error');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/auth/setup/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setupToken, code }),
      });
      const body = (await res.json()) as { error?: string; username?: string };
      if (!res.ok) {
        setError(body.error ?? 'verification failed');
        return;
      }
      setCreatedUser(body.username ?? username);
      setStep('done');
    } catch {
      setError('network error');
    } finally {
      setBusy(false);
    }
  };

  if (step === 'closed') {
    return (
      <div className="sd-adduser">
        <button type="button" className="sd-btn" onClick={() => setStep('form')}>
          Add user…
        </button>
        <div className="sd-hint">A second account, for real-time co-editing later.</div>
      </div>
    );
  }

  if (step === 'form') {
    return (
      <form className="sd-adduser" onSubmit={(e) => void begin(e)}>
        <div className="settings-field">
          <label htmlFor="sd-new-username">New username</label>
          <input
            id="sd-new-username"
            value={username}
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div className="settings-field">
          <label htmlFor="sd-new-password">Password (min 10 chars)</label>
          <input
            id="sd-new-password"
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && <div className="sd-error">{error}</div>}
        <div className="sd-btn-row">
          <button
            type="submit"
            className="sd-btn primary"
            disabled={busy || !username || password.length < 10}
          >
            Continue
          </button>
          <button type="button" className="sd-btn" onClick={reset}>
            Cancel
          </button>
        </div>
      </form>
    );
  }

  if (step === 'totp') {
    return (
      <form className="sd-adduser" onSubmit={(e) => void verify(e)}>
        <div className="sd-hint">
          Scan with the new user's authenticator app, then enter the 6-digit code.
        </div>
        {qrDataUrl && (
          <div className="sd-qr">
            <img src={qrDataUrl} alt="TOTP QR code" width={200} height={200} />
          </div>
        )}
        <div className="sd-secret">{secret}</div>
        <div className="settings-field">
          <label htmlFor="sd-totp-code">6-digit code</label>
          <input
            id="sd-totp-code"
            value={code}
            inputMode="numeric"
            autoComplete="one-time-code"
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        {error && <div className="sd-error">{error}</div>}
        <div className="sd-btn-row">
          <button type="submit" className="sd-btn primary" disabled={busy || code.length < 6}>
            Verify &amp; create
          </button>
          <button type="button" className="sd-btn" onClick={reset}>
            Cancel
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="sd-adduser">
      <div className="sd-success">User “{createdUser}” created — they can log in now.</div>
      <button type="button" className="sd-btn" onClick={reset}>
        Done
      </button>
    </div>
  );
}
